"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageWhyPropTradingSchema } from "@/sanity/pages/PageWhyPropTradingTemplate";
import dotsBg from "@/public/home/dots-bg-dark.png";
import TextScrollFadeIn from "@/components/global/partials/TextScrollFadeIn";

const ease = [0.25, 0.1, 0.25, 1] as const;

const IQLogo = () => (
  <div className="flex-shrink-0 w-[9.25rem] h-[9.5rem] bg-black flex items-center justify-center px-[1.75rem]">
    <svg
      viewBox="0 0 37.9935 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
    >
      <path
        d="M0 29.9967V1.04476H7.07592V15.5207V29.9967H0Z"
        fill="url(#iq-grad-a)"
      />
      <path
        d="M31.2144 32L29.0174 29.3093C28.4236 29.6072 27.6715 29.8356 26.7808 30.0043C25.8902 30.1632 24.9005 30.2526 23.8119 30.2526C20.942 30.2526 18.4283 29.6171 16.2907 28.3562C14.1531 27.0953 12.5004 25.318 11.3326 23.0344C10.1648 20.7509 9.58092 18.1297 9.58092 15.161C9.58092 12.1924 10.1252 9.70028 11.2039 7.41669C12.2826 5.13311 13.8957 3.32609 16.0235 1.99566C18.1512 0.665219 20.7441 0 23.8119 0C26.8798 0 29.4628 0.665219 31.5707 1.99566C33.6885 3.32609 35.2818 5.13311 36.3704 7.41669C37.4492 9.70028 37.9935 12.2817 37.9935 15.161C37.9935 17.8517 37.5481 20.1849 36.6475 22.1508C35.7569 24.1266 34.4901 25.7847 32.8671 27.1449L36.7663 32H31.1946H31.2144ZM23.8218 24.4542C24.1187 24.4542 24.3958 24.4344 24.6333 24.3947C24.8807 24.3549 25.1084 24.2954 25.3261 24.206L21.3873 19.3906H26.959L28.7502 21.7536C29.3737 21.0785 29.8586 20.2147 30.2149 19.1623C30.5712 18.1198 30.7394 16.7794 30.7394 15.1412C30.7394 13.5029 30.5019 11.9442 30.0269 10.5343C29.5518 9.12442 28.8096 7.9727 27.7903 7.08905C26.771 6.2054 25.4547 5.76854 23.8317 5.76854C22.2087 5.76854 20.843 6.21533 19.8237 7.08905C18.8044 7.9727 18.0621 9.12442 17.5871 10.5343C17.1121 11.9442 16.8746 13.4831 16.8746 15.1412C16.8746 16.7993 17.122 18.2985 17.6069 19.7083C18.0918 21.1182 18.8439 22.26 19.8633 23.1337C20.8826 24.0074 22.1988 24.4344 23.8218 24.4344V24.4542Z"
        fill="url(#iq-grad-b)"
      />
      <defs>
        <linearGradient
          id="iq-grad-a"
          x1="3.53796"
          y1="1.04476"
          x2="3.53796"
          y2="29.9967"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#E0E0E0" />
        </linearGradient>
        <linearGradient
          id="iq-grad-b"
          x1="23.7872"
          y1="0"
          x2="23.7872"
          y2="32"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#E0E0E0" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export default function IntroSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.introDescription && !data?.introSteps?.length) return null;

  return (
    <section className="relative w-full flex flex-col items-center bg-white py-[8rem] lg:py-[15rem] px-[2rem] lg:px-[6rem]">
      <div className="w-[min(83.5rem,100%)] flex flex-col gap-[6rem] lg:gap-[4rem]">
        {/* IQ logo + intro description */}
        {data?.introDescription && (
          <div className="flex flex-col gap-[2.5rem]">
            <IQLogo />
            <TextScrollFadeIn
              portableText={data.introDescription}
              className="richtext richtext-start prose-p:text-[#103037] prose-p:text-[3rem] lg:prose-p:text-[3rem] prose-headings:text-[3rem] prose-p:font-medium prose-p:leading-[1.3] prose-p:tracking-[-0.16rem]"
            />
          </div>
        )}

        <div className="w-full h-[0.125rem] bg-black/10"></div>

        {/* How Does It Work */}
        {!!data?.introSteps?.length && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="flex flex-col gap-[3.5rem]"
          >
            {data?.introHowItWorksTitle && (
              <h2 className="mb-0 text-[3rem] lg:text-[4rem] mt-[2rem] font-medium leading-[1.3] tracking-[-0.16rem] text-[#103037]">
                {data.introHowItWorksTitle}
              </h2>
            )}

            <ul className="flex flex-col gap-[1.75rem] list-none p-0 m-0">
              {data.introSteps.map((step, i) => (
                <li
                  key={step._key ?? i}
                  className="relative bg-[#04271d] overflow-hidden p-[4rem] flex flex-col gap-[2.375rem]"
                >
                  <div
                    className="absolute inset-0 opacity-20 pointer-events-none mix-blend-exclusion"
                    aria-hidden="true"
                  >
                    <Image
                      src={dotsBg}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="relative z-10 flex flex-col gap-[2.375rem]">
                    <div className="bg-white flex items-center justify-center w-[3.5rem] px-[0.75rem] py-[1rem]">
                      <p className="mb-0 font-medium text-[1.75rem] text-black tracking-[-0.0875rem] leading-[1.4] whitespace-nowrap">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                    </div>

                    {step.description && (
                      <div className="richtext richtext-start prose-p:text-white/80 prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:tracking-[-0.1125rem]">
                        <PortableText
                          value={step.description}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Footnote */}
        {data?.introFootnote && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, ease, delay: 0.1 }}
            className="richtext richtext-start"
          >
            <PortableText
              value={data.introFootnote}
              components={richTextComponent}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
